import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, NavLink, useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import { Loader, Alert, ProgressBar } from '../../components/ui.jsx';
import QuizPage from '../../lessons/QuizPage.jsx';
import {
  IntroPage,
  TeachPage,
  CardsPage,
  ChoicePage,
  SortPage,
  SpotPage,
  MatchPage,
} from '../../lessons/pages.jsx';
import { OrderPage, RatePage, DesignPage } from '../../lessons/activities.jsx';
import { TriagePage, AuditPage } from '../../lessons/practical.jsx';
import { LabPage } from '../../lessons/lab.jsx';
import { usePracticals, canRemember } from '../../lib/practical.js';

/** Page types that must be finished before Next unlocks. */
const INTERACTIVE = new Set([
  'choice', 'sort', 'spot', 'match', 'order', 'rate', 'design', 'triage', 'audit',
]);

/** Page types whose work is saved to the server, and the kind it is stored as. */
const PROJECT_KIND = { design: 'sticker-pack', audit: 'device-audit' };

const RENDERERS = {
  intro: IntroPage,
  teach: TeachPage,
  cards: CardsPage,
  choice: ChoicePage,
  sort: SortPage,
  spot: SpotPage,
  match: MatchPage,
  order: OrderPage,
  rate: RatePage,
  design: DesignPage,
  triage: TriagePage,
  audit: AuditPage,
  // `lab` is deliberately absent: LabPage gets a branch of its own
  // below, because it has to be told whether its practical is finished.
};

export default function LessonPlayer() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);
  const [doneActivities, setDoneActivities] = useState({});
  const [project, setProject] = useState(null);
  const [projectReady, setProjectReady] = useState(false);
  // Just the module's name, for the link back out of the rail.
  const [moduleName, setModuleName] = useState(null);
  // How far through the lesson they have actually been, so the rail
  // can move them about without skipping past a gated activity.
  const [furthest, setFurthest] = useState(0);
  // Whether they have finished this lesson before. Practicals became
  // compulsory after some children had already been through, and
  // sending them back to redo one to reach a quiz they have already
  // passed would be a punishment for somebody else's change.
  const [lessonDone, setLessonDone] = useState(false);

  const startedAt = useRef(Date.now());
  const savedPage = useRef(-1);
  const topRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLesson(null);
    setIndex(0);
    setDoneActivities({});
    setProject(null);
    setProjectReady(false);
    setFurthest(0);
    setLessonDone(false);

    // A practical-task lesson may already have saved work to restore.
    api
      .loadProject(slug)
      .then((d) => alive && setProject(d.project?.items ?? null))
      .catch(() => {})
      .finally(() => alive && setProjectReady(true));

    // The lesson endpoint does not name the module it belongs to, so
    // it is found from the course tree. This fails quietly: it only
    // feeds a link, and must never stop a lesson opening.
    api
      .tracks()
      .then(({ tracks }) => {
        if (!alive) return;
        for (const track of tracks) {
          const mod = track.modules.find((m) => m.lessons.some((l) => l.slug === slug));
          if (mod) {
            setModuleName(mod.title);
            return;
          }
        }
      })
      .catch(() => {});

    api
      .lesson(slug)
      .then((data) => {
        if (!alive) return;
        setLesson(data.lesson);
        setLessonDone(data.progress?.status === 'completed');
        // Drop the child back where they stopped, but never on the quiz.
        const resume = data.progress?.lastPage ?? 0;
        const lastTeachingPage = data.lesson.pages.findIndex((p) => p.type === 'quiz');
        const cap = lastTeachingPage === -1 ? data.lesson.pages.length - 1 : lastTeachingPage - 1;
        const start = Math.min(Math.max(0, resume), Math.max(0, cap));
        setIndex(start);
        setFurthest(start);
      })
      .catch((err) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, [slug]);

  // Bookmark progress whenever the child moves to a new furthest page.
  useEffect(() => {
    if (!lesson || index <= savedPage.current) return;
    savedPage.current = index;
    const seconds = Math.round((Date.now() - startedAt.current) / 1000);
    startedAt.current = Date.now();
    api.saveProgress(slug, index, Math.min(seconds, 3600)).catch(() => {});
  }, [index, lesson, slug]);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [index]);

  const markActivityDone = useCallback(() => {
    setDoneActivities((d) => (d[index] ? d : { ...d, [index]: true }));
  }, [index]);

  const saveProject = useCallback(
    (kind) => (items) => api.saveProject(slug, { kind, items }),
    [slug],
  );

  const submitQuiz = useCallback(
    (answers) => {
      const seconds = Math.round((Date.now() - startedAt.current) / 1000);
      return api.submitQuiz(slug, answers, Math.min(seconds, 3600));
    },
    [slug],
  );

  /*
   * Practicals open in a tab of their own, so the lesson cannot watch
   * the work happen - it learns they are finished through
   * lib/practical.js. Every practical in the lesson is watched at
   * once rather than just the page being looked at, which is what
   * lets the rail tick off the ones already done.
   */
  const labPaths = useMemo(
    () =>
      (lesson?.pages ?? [])
        .filter((p) => p.type === 'lab' && p.site?.href)
        .map((p) => p.site.href),
    [lesson],
  );
  const practicalsDone = usePracticals(labPaths);

  if (error) {
    return (
      <>
        <Alert kind="bad">{error}</Alert>
        <Link to="/learn" className="btn btn-ghost">← Back to my courses</Link>
      </>
    );
  }
  if (!lesson) return <Loader label="Opening your lesson…" />;

  // Exiting should land back in the course, not up at the course list.
  const courseHref = lesson.trackSlug ? `/learn/course/${lesson.trackSlug}` : '/learn';
  const page = lesson.pages[index];
  const isQuiz = page.type === 'quiz';
  const total = lesson.pages.length;
  const needsActivity = INTERACTIVE.has(page.type) && !doneActivities[index];
  const Renderer = RENDERERS[page.type];

  const practicalHref = page.type === 'lab' ? page.site?.href ?? null : null;
  const practicalDone = practicalHref ? Boolean(practicalsDone[practicalHref]) : true;

  /* A practical has to be finished, not merely opened - which is the
     whole point of it being a practical. Only on the way through
     though: a lesson already completed is not re-locked. */
  const mustFinish = Boolean(practicalHref) && !lessonDone;

  /* The fallback is for a browser that will not keep the finished flag
     at all - a private window, or site data blocked. There the lesson
     asks only that the practical was opened, because gating on a flag
     that can never be written would wall the child in with no way
     out. */
  const needsPractical = mustFinish
    ? (canRemember ? !practicalDone : !doneActivities[index])
    : false;
  const blocked = needsActivity || needsPractical;

  /* A short label for each section, so the rail reads as a contents
     list rather than a row of identical entries. */
  const SECTION_KIND = {
    intro: 'Start', teach: 'Read', cards: 'Read', lab: 'Practical',
    quiz: 'Quiz', choice: 'Activity', sort: 'Activity', spot: 'Activity',
    match: 'Activity', order: 'Activity', rate: 'Activity', design: 'Task',
    triage: 'Activity', audit: 'Task',
  };

  /* Inside a topic the same labels read differently: a page is not
     "Read" as opposed to something else, it is the theory before the
     practical. */
  const SUB_KIND = { ...SECTION_KIND, teach: 'Theory', cards: 'Theory' };

  /* Consecutive pages sharing a title are one topic in the rail, with
     those pages listed under it as numbered sub-sections. A subject
     taught and then practised is one thing to a reader, even though
     the player holds it as two pages. */
  const topics = [];
  lesson.pages.forEach((p, i) => {
    const title = p.title ?? SECTION_KIND[p.type] ?? 'Section';
    const last = topics[topics.length - 1];
    if (last && last.title === title) last.items.push({ page: p, index: i });
    else topics.push({ title, items: [{ page: p, index: i }] });
  });

  /** The open-in-a-new-tab link a practical page carries, if any. */
  const siteLink = (p, shut) =>
    p.site?.href && !shut ? (
      <a
        href={p.site.href}
        target="_blank"
        rel="noopener noreferrer"
        className="topic-sub"
      >
        <span className="topic-sub-mark" aria-hidden="true">
          {p.type === 'lab' && practicalsDone[p.site.href] ? '✓' : '🗔'}
        </span>
        {p.site.label ?? 'Practical'}
        <span className="topic-sub-hint">opens in a new tab</span>
      </a>
    ) : null;

  return (
    <div className="player-layout">
      {/* This lesson's own sections. Anything already reached can be
          jumped back to; anything beyond stays shut, or the rail would
          walk straight past an activity that has to be finished. */}
      {lesson.pages.length > 1 && (
        <nav className="topic-rail" aria-label="Sections in this lesson">
          <Link to={courseHref} className="topic-rail-back">
            ← {moduleName ?? 'Back to the module'}
          </Link>
          <span className="topic-rail-lesson">{lesson.title}</span>
          <ul>
            {topics.map((topic) => {
              const first = topic.items[0].index;
              const last = topic.items[topic.items.length - 1].index;
              const here = index >= first && index <= last;
              const seen = last < furthest;
              const shut = first > furthest;
              const mark = shut ? '🔒' : seen ? '✓' : first + 1;
              const solo = topic.items.length === 1;

              return (
                <li key={`${topic.title}-${first}`}>
                  <button
                    type="button"
                    className={`topic ${seen ? 'is-done' : ''} ${here ? 'is-current' : ''} ${shut ? 'is-locked' : ''}`}
                    onClick={() => !shut && setIndex(first)}
                    disabled={shut}
                    aria-current={here && solo ? 'step' : undefined}
                  >
                    <span className="topic-mark">{mark}</span>
                    <span className="topic-name">
                      {topic.title}
                      {solo && (
                        <span className="topic-kind">
                          {SECTION_KIND[topic.items[0].page.type] ?? topic.items[0].page.type}
                        </span>
                      )}
                    </span>
                  </button>

                  {/* A practical opens a site of its own, so the rail
                      offers it directly rather than making them walk to
                      the section first and find the button there. */}
                  {solo && siteLink(topic.items[0].page, shut)}

                  {!solo && (
                    <ol className="topic-steps">
                      {topic.items.map(({ page: p, index: i }, n) => {
                        const stepShut = i > furthest;
                        return (
                          <li key={`${p.type}-${i}`}>
                            <button
                              type="button"
                              className={`topic-step ${i < furthest ? 'is-done' : ''} ${i === index ? 'is-current' : ''} ${stepShut ? 'is-locked' : ''}`}
                              onClick={() => !stepShut && setIndex(i)}
                              disabled={stepShut}
                              aria-current={i === index ? 'step' : undefined}
                            >
                              <span className="topic-step-n">{n + 1}</span>
                              {SUB_KIND[p.type] ?? p.type}
                            </button>
                            {siteLink(p, stepShut)}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      )}

    <div className="player" ref={topRef}>
      {/* ---------- header ---------- */}
      <div className={`player-head t-${lesson.color}`}>
        <Link to={courseHref} className="btn btn-sm btn-ghost">← Exit</Link>
        <div className="player-title">
          <span aria-hidden="true">{lesson.icon}</span>
          <div>
            <strong>{lesson.title}</strong>
            <span className="small faint">
              Step {index + 1} of {total}
            </span>
          </div>
        </div>
        <span className="pill">⭐ {lesson.xpReward} XP</span>
      </div>

      <div className="player-progress">
        <ProgressBar value={index + 1} max={total} tone={lesson.color} label={false} />
      </div>

      {/* ---------- slide ---------- */}
      <div className="player-body card">
        {isQuiz ? (
          <QuizPage
            page={page}
            lesson={lesson}
            onSubmit={submitQuiz}
            courseHref={courseHref}
            onFinished={() => setDoneActivities((d) => ({ ...d, [index]: true }))}
          />
        ) : PROJECT_KIND[page.type] ? (
          // Wait for any saved work before mounting, so the task starts
          // from what the child already did rather than from blank.
          projectReady && (
            <Renderer
              key={slug}
              page={page}
              onComplete={markActivityDone}
              onSave={saveProject(PROJECT_KIND[page.type])}
              saved={project}
            />
          )
        ) : page.type === 'lab' ? (
          <LabPage
            page={page}
            onComplete={markActivityDone}
            done={practicalDone}
            required={mustFinish}
            gated={canRemember}
          />
        ) : (
          <Renderer page={page} onComplete={markActivityDone} />
        )}
      </div>

      {/* ---------- footer nav (the quiz owns its own buttons) ---------- */}
      {!isQuiz && (
        <div className="player-nav">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
          >
            ← Back
          </button>

          <div className="player-dots" aria-hidden="true">
            {lesson.pages.map((p, i) => (
              <span key={i} className={`player-dot ${i === index ? 'is-now' : ''} ${i < index ? 'is-done' : ''}`} />
            ))}
          </div>

          {blocked ? (
            <span className="player-lock">
              {needsPractical
                ? 'Finish the practical to continue 👆'
                : 'Finish the activity to continue 👆'}
            </span>
          ) : index === total - 1 ? (
            <button type="button" className="btn" onClick={() => navigate(courseHref)}>
              Finish lesson ✓
            </button>
          ) : (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setIndex((i) => i + 1);
                setFurthest((f) => Math.max(f, index + 1));
              }}
            >
              {lesson.pages[index + 1]?.type === 'quiz' ? 'Start the quiz 🎯' : 'Next →'}
            </button>
          )}
        </div>
      )}
    </div>
    </div>
  );
}
